import { Entity_Kind } from '@gen/chat/v1/common_pb';
import { MAX_ENTITY_COUNT } from '@/protocol/limits';
import { hexToUuid } from '@/store/ids';
import type { StoredEntity } from '@/store/types';

/**
 * Text entities, measured the way the protocol measures them.
 *
 * Section 06: "Text is UTF-8 with entities using Unicode scalar-value offsets
 * and lengths, not UTF-16 code units." JavaScript strings are UTF-16, so every
 * offset a browser naturally produces — `indexOf`, `selectionStart`, `length` —
 * is the wrong unit the moment the text contains an emoji or any character
 * outside the basic multilingual plane. A mention placed after one would land
 * one position late on every other client.
 *
 * The conversion is done once, here, so no screen has to remember it.
 */

/** Scalar-value length of a string. */
export function scalarLength(text: string): number {
  let count = 0;
  for (const _ of text) {
    count += 1;
  }
  return count;
}

/** Converts a UTF-16 code-unit index into a Unicode scalar-value index. */
export function scalarOffsetOf(text: string, utf16Index: number): number {
  let scalars = 0;
  let index = 0;
  for (const character of text) {
    if (index >= utf16Index) {
      return scalars;
    }
    index += character.length;
    scalars += 1;
  }
  return scalars;
}

/**
 * Finds the mention being typed at the caret.
 *
 * Returns null unless the caret sits inside an `@word` run that began at a word
 * boundary. Requiring the boundary is what stops an email address from opening
 * a member picker on every keystroke.
 */
export function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) {
    return null;
  }
  const preceding = at === 0 ? '' : before[at - 1];
  if (preceding !== undefined && preceding !== '' && !/\s/u.test(preceding)) {
    return null;
  }
  const query = before.slice(at + 1);
  if (/\s/u.test(query)) {
    return null;
  }
  return { start: at, query };
}

/**
 * Replaces the `@query` run with a display name and produces its entity.
 *
 * The entity value is the canonical UUID form of the user identifier, which is
 * what Section 15 fixes for `MENTION`; the store's hexadecimal form is an
 * internal representation and is never what goes on the wire.
 */
export function applyMention(
  text: string,
  start: number,
  caret: number,
  userIdHex: string,
  displayName: string,
): { text: string; caret: number; entity: StoredEntity } {
  const label = `@${displayName}`;
  const next = `${text.slice(0, start)}${label} ${text.slice(caret)}`;
  return {
    text: next,
    caret: start + label.length + 1,
    entity: {
      kind: Entity_Kind.MENTION,
      offset: scalarOffsetOf(next, start),
      // The entity covers the displayed mention text, not the trailing space.
      length: scalarLength(label),
      value: hexToUuid(userIdHex),
    },
  };
}

/**
 * Re-anchors entities against the current text and drops the ones that no
 * longer match.
 *
 * A user who edits the text around a mention — or deletes part of the mention
 * itself — must not send an entity pointing at a range that no longer holds the
 * name. Rather than tracking edits, the entity is re-verified against the text
 * at send time: the range must still read as the mention's own label. Anything
 * else is discarded, and the mention degrades to ordinary text, which is
 * exactly the Phase 1 fallback Section 15 describes.
 */
export function verifyEntities(
  text: string,
  entities: readonly (StoredEntity & { label?: string })[],
): StoredEntity[] {
  const characters = [...text];
  const kept: StoredEntity[] = [];
  for (const entity of entities) {
    if (kept.length >= MAX_ENTITY_COUNT) {
      break;
    }
    const end = entity.offset + entity.length;
    if (entity.offset < 0 || end > characters.length) {
      continue;
    }
    const slice = characters.slice(entity.offset, end).join('');
    if (entity.label !== undefined && slice !== entity.label) {
      continue;
    }
    kept.push({
      kind: entity.kind,
      offset: entity.offset,
      length: entity.length,
      value: entity.value,
    });
  }
  return kept;
}
